import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AppContext';
import LoadingSpinner from '../common/LoadingSpinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requireVerification?: boolean;
  facultyOnly?: boolean;
  studentOnly?: boolean;
}

/**
 * ProtectedRoute component to handle authentication and authorization
 *
 * Flow:
 * 1. If emailVerified = false -> redirect to /verify-otp
 * 2. If emailVerified = true but isVerified = false -> redirect to /pending-approval
 * 3. If emailVerified = true and isVerified = true -> allow access
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  requireVerification = true,
  facultyOnly = false,
  studentOnly = false,
}) => {
  const { user, isAuthenticated, isVerified, isFaculty, isStudent, loading } = useAuth();
  const location = useLocation();

  // Show loading spinner while auth state is being determined
  if (loading) {
    return <LoadingSpinner fullScreen message="Loading..." />;
  }

  // If authentication is required but user is not authenticated
  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If user is authenticated, check email verification first
  if (requireAuth && isAuthenticated && user) {
    // Check if email is not verified (emailVerified = false)
    // This should redirect to login OTP verification
    if ((user as any).emailVerified === false) {
      return <Navigate to="/login-otp" state={{
        email: user.email,
        name: user.name
      }} replace />;
    }

    // Email is verified, now check if management approval is needed (isVerified)
    if (requireVerification && !isVerified) {
      return <Navigate to="/pending-approval" replace />;
    }
  }

  // If faculty-only access is required but user is not faculty
  if (facultyOnly && !isFaculty) {
    return <Navigate to="/clubs" replace />;
  }

  // If student-only access is required but user is not student
  if (studentOnly && !isStudent) {
    return <Navigate to="/clubs" replace />;
  }

  // If user is authenticated but trying to access auth pages, redirect to clubs
  if (!requireAuth && isAuthenticated && isVerified) {
    return <Navigate to="/clubs" replace />;
  }

  // All checks passed, render the protected content
  return <>{children}</>;
};

export default ProtectedRoute;